
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import ymaps from 'yandex-maps'

// @components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'

// @new-components
import NewMap from '@/common/new-components/NewMap/index.vue'

// @types
import { CityWithCoordinates, RegionT, UpdateCityPayloadT } from '@/core/types/cities.types'
import { DispatcherSector } from '@/new-services/types/dispatcher-sectors'

// @realisations
import { cities } from '@/core/realisations/cities.realisation'
import { geocoder } from '@/core/realisations/geocoder.realisation'

import dispatcherSectorsAPI from '@/new-services/dispatcher-sectors.service'

// @validation
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'

// @constants
import { CitiesTypes } from '@/core/constants/cities.constants'

// @composable
import useNotifications from '@/common/composable/useNotifications'
import useMap from '@/common/new-composable/useMap'
import useMapGeoObjects from '@/common/new-composable/useMapGeoObjects'
import { useDebounce } from 'vue-composable'

export const STATUS_OPTIONS = [
    { id: 1, name: 'Активный' },
    { id: 2, name: 'Неактивный' },
]

export default defineComponent({
    components: {
        PageTitle,
        TheButton,
        TheInput,
        TheSelect,
        NewMap,
        Loader,
        Checkbox,
    },
    setup() {
        let center_map_instance = null as ymaps.Map | null
        let border_map_instance = null as ymaps.Map | null

        const map_graphics = useMapGeoObjects()
        const { addGeoObject, clearMap, setMapCenter } = useMap()

        const notifications = useNotifications()

        const router = useRouter()
        const route = useRoute()

        const city_id = Number(route.params.city_id)

        const city_state = reactive({
            is_loading: false,
            is_failed: false,
            city: null as CityWithCoordinates | null,
        })

        const state = reactive({
            is_loading: false,
            is_center_map_ready: false,
            is_border_map_ready: false,
            name: null as string | null,
            nameKz: null as string | null,
            nameEn: null as string | null,
            nameZh: null as string | null,
            region_id: null as number | null,
            type_id: null as number | null,
            airCode: null as string | null,
            postCode: null as string | null,
            statusId: null as number | null,
            isAviaEnable: false,
            map_center: [43.25391893213578, 76.92523545812777],
            rectangle_coords: [
                [43.370716, 76.735022],
                [43.126899, 77.196363],
            ],
            dispatcherSectorId: null as number | null,
        })

        const search_state = reactive({
            query: null as string | null,
            is_loading: false,
            is_failed: false,
            results: [] as {
                name: string
                street: string | null
                description?: string
                coordinates: number[]
            }[],
        })

        const data_state = reactive({
            regions: {
                is_loading: false,
                is_failed: false,
                list: [] as RegionT[],
            },
            dispatcher_sectors: {
                is_loading: false,
                is_failed: false,
                list: [] as DispatcherSector[]
            }
        })

        const state_form$ = useVuelidate(
            {
                name: { required },
                nameKz: { required },
                nameEn: { required },
                nameZh: { required },
                region_id: { required },
                type_id: { required },
            },
            state
        )

        const loadRegions = async () => {
            try {
                data_state.regions.is_loading = true

                const data = await cities.getRegions()
                data_state.regions.list = data.data

                data_state.regions.is_failed = false
            } catch (error) {
                console.error(error)
                data_state.regions.is_failed = true
                notifications.notifyError({
                    error,
                    title: 'При получении регионов произошла ошибка',
                })
            } finally {
                data_state.regions.is_loading = false
            }
        }

        const loadDispatcherSectors = async () => {
            try {
                data_state.dispatcher_sectors.is_loading = true

                const response = await dispatcherSectorsAPI.getDispatcherSectors({ page: 1, limit: 9999 })
                data_state.dispatcher_sectors.list = response.data.data

                data_state.dispatcher_sectors.is_failed = false
            } catch (error) {
                console.error(error)
                data_state.dispatcher_sectors.is_failed = true
                notifications.notifyError({
                    error,
                    title: 'При получении диспетчерских секторов произошла ошибка',
                })
            } finally {
                data_state.dispatcher_sectors.is_loading = false
            }
        }

        const onBack = () => {
            router.push('/cities')
        }

        const onMapReady = (instance: ymaps.Map) => {
            center_map_instance = instance
            state.is_center_map_ready = true

            setMapCenter(center_map_instance, state.map_center)
            createPlacemark()
        }

        const createPlacemark = () => {
            clearMap(center_map_instance as ymaps.Map)

            const placemark = map_graphics.createPoint(
                state.map_center,
                {
                    iconContent: 'Центр города',
                },
                {
                    preset: 'islands#blueStretchyIcon',
                }
            )

            addGeoObject(center_map_instance as ymaps.Map, placemark)
        }

        const onMapClick = (
            event: MouseEvent | ymaps.IEvent<Record<any, any>, Record<any, any>>
        ) => {
            // @ts-ignore
            const coordinates = event.get('coords')
            state.map_center = coordinates

            createPlacemark()

            setMapCenter(center_map_instance as ymaps.Map, coordinates)
            setMapCenter(border_map_instance as ymaps.Map, coordinates)
        }

        const createBordersRectangle = () => {
            // @ts-ignore
            const rectangle = new window.ymaps.Rectangle(
                state.rectangle_coords,
                {
                    hintContent: 'Границы области просмотра города',
                },
                {
                    draggable: true,
                    fillColor: '#33A4FF',
                    fillOpacity: 0.2,
                    strokeColor: '#297ABC',
                    strokeWidth: 1,
                }
            )

            rectangle.events.group().add('dragend', moveBordersRectangleByDragEnd)
            addGeoObject(border_map_instance as ymaps.Map, rectangle)
        }

        const rerenderRectangle = () => {
            clearMap(border_map_instance as ymaps.Map)
            createBordersRectangle()
        }

        const onIncreaseRectangle = () => {
            const current_coords = state.rectangle_coords

            const new_coords = [
                [current_coords[0][0], current_coords[0][1]],
                [current_coords[1][0] - 0.03, current_coords[1][1] + 0.1],
            ]

            state.rectangle_coords = new_coords
            rerenderRectangle()
        }

        const onDecreaseRectangle = () => {
            const current_coords = state.rectangle_coords

            if (isDecreaseDisabled.value) {
                return
            }

            const new_coords = [
                [current_coords[0][0], current_coords[0][1]],
                [current_coords[1][0] + 0.03, current_coords[1][1] - 0.1],
            ]

            state.rectangle_coords = new_coords
            rerenderRectangle()
        }

        const onMoveToCenterRectangle = () => {
            const difference = [
                state.rectangle_coords[0][0] - state.rectangle_coords[1][0],
                state.rectangle_coords[1][1] - state.rectangle_coords[0][1],
            ]

            const new_coords = [
                [state.map_center[0], state.map_center[1]],
                [state.map_center[0] - difference[0], state.map_center[1] + difference[1]],
            ]

            state.rectangle_coords = new_coords
            rerenderRectangle()
        }

        const moveBordersRectangleByDragEnd = () => {
            const rectangle = (border_map_instance as ymaps.Map).geoObjects.get(0)
            if (!rectangle) return

            // @ts-ignore
            const coordinates = rectangle.geometry.getCoordinates()

            state.rectangle_coords = coordinates
        }

        const onBorderMapReady = (instance: ymaps.Map) => {
            border_map_instance = instance
            state.is_border_map_ready = true

            setMapCenter(border_map_instance as ymaps.Map, state.map_center)

            createBordersRectangle()
        }

        const moveBordersRectangle = (coordinates: number[]) => {
            const difference = [
                state.rectangle_coords[0][0] - state.rectangle_coords[1][0],
                state.rectangle_coords[1][1] - state.rectangle_coords[0][1],
            ]

            const new_coords = [
                [coordinates[0], coordinates[1]],
                [coordinates[0] - difference[0], coordinates[1] + difference[1]],
            ]

            state.rectangle_coords = new_coords
            rerenderRectangle()
        }

        const onBorderMapClick = (
            event: MouseEvent | ymaps.IEvent<Record<any, any>, Record<any, any>>
        ) => {
            // @ts-ignore
            const coordinates = event.get('coords')
            moveBordersRectangle(coordinates)
        }

        const formatCoordinatesFromString = (coords_string: string): number[] => {
            const coords = coords_string.split(' ')
            const latitude = Number(coords[1])
            const longitude = Number(coords[0])

            return [latitude, longitude]
        }

        const geocodeAddress = async () => {
            if (!search_state.query) {
                search_state.is_loading = false
                search_state.is_failed = false
                search_state.results = []
                return
            }

            try {
                search_state.is_loading = true

                let query = search_state.query
                const response = await geocoder.yandexGeocode(query, null)

                if (response.response.GeoObjectCollection.featureMember.length === 0) {
                    search_state.results = []
                    return
                }

                search_state.results = response.response.GeoObjectCollection.featureMember.map(
                    (feature_member) => {
                        const address_components =
                            feature_member.GeoObject.metaDataProperty.GeocoderMetaData.Address
                                .Components
                        const object_address_components: Record<string, string> = {}

                        address_components.forEach((component) => {
                            object_address_components[component.kind] = component.name
                        })

                        return {
                            name: feature_member.GeoObject.name,
                            street: object_address_components.street || null,
                            description: feature_member.GeoObject.description,
                            coordinates: formatCoordinatesFromString(
                                feature_member.GeoObject.Point.pos
                            ),
                        }
                    }
                )

                search_state.is_failed = false
            } catch (error) {
                console.error(error)
                search_state.is_failed = true
            } finally {
                search_state.is_loading = false
            }
        }

        const geocode = useDebounce(geocodeAddress, 1000)

        const onSelectAddress = (address: {
            name: string
            street: string | null
            description?: string
            coordinates: number[]
        }) => {
            state.map_center = address.coordinates

            createPlacemark()

            setMapCenter(center_map_instance as ymaps.Map, address.coordinates)
            setMapCenter(border_map_instance as ymaps.Map, address.coordinates)

            search_state.query = null
            search_state.results = []
        }

        const isDecreaseDisabled = computed(() => {
            if (
                state.rectangle_coords[0][0] - state.rectangle_coords[1][0] + 0.03 <= 0.303 ||
                state.rectangle_coords[1][1] - state.rectangle_coords[0][1] - 0.1 <= 0.361
            ) {
                return true
            }

            return false
        })

        const loadCity = async () => {
            try {
                city_state.is_loading = true

                const response = await cities.getCity(city_id)
                city_state.city = response.data

                state.name = response.data.nameRu
                state.nameKz = response.data.nameKz
                state.nameEn = response.data.nameEn
                state.nameZh = response.data.nameZh
                state.region_id = response.data.regionId
                state.type_id = response.data.typeId
                state.airCode = response.data.airCode
                state.postCode = response.data.postCode
                state.statusId = response.data.statusId
                state.isAviaEnable = response.data.aviaEnabled
                state.dispatcherSectorId = response.data.dispatcherSectorId

                if (response.data.latitude && response.data.longitude) {
                    state.map_center = [response.data.latitude, response.data.longitude]
                }

                if (response.data.coordinates) {
                    state.rectangle_coords = response.data.coordinates
                }

                loadRegions()
                loadDispatcherSectors()

                city_state.is_failed = false
            } catch (error) {
                city_state.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении данных о городе произошла ошибка',
                })
            } finally {
                city_state.is_loading = false
            }
        }

        const updateCity = async () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            const answer = confirm(
                'Вы уверены что установили центр и границы области просмотра города корректно?'
            )
            if (!answer) return

            try {
                state.is_loading = true

                const payload: UpdateCityPayloadT = {
                    nameRu: state.name as string,
                    nameKz: state.nameKz as string,
                    nameEn: state.nameEn as string,
                    nameZh: state.nameZh as string,
                    typeId: state.type_id as number,
                    airCode: state.airCode,
                    postCode: state.postCode as string,
                    statusId: state.statusId,
                    isAviaEnable: state.isAviaEnable,
                    regionId: state.region_id as number,
                    latitude: state.map_center[0],
                    longitude: state.map_center[1],
                    coordinates: state.rectangle_coords,
                    dispatcherSectorId: state.dispatcherSectorId
                }

                await cities.updateCity(city_id, payload)

                notifications.notify({ type: 'success', title: 'Город сохранен' })
                onBack()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При сохранении города произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        onBeforeMount(loadCity)

        return {
            city_state,
            CitiesTypes,
            state,
            search_state,
            state_form$,
            data_state,
            loadRegions,
            onBack,
            onMapReady,
            onMapClick,
            onBorderMapReady,
            onBorderMapClick,
            onIncreaseRectangle,
            onDecreaseRectangle,
            onMoveToCenterRectangle,
            geocode,
            onSelectAddress,
            isDecreaseDisabled,
            updateCity,
            loadCity,
            loadDispatcherSectors,
            STATUS_OPTIONS,
        }
    },
})
